import React, { useState, useEffect, useCallback } from 'react';
import { X, Cookie, Shield, BarChart3, Megaphone } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Cookie & Tracking Consent — AVG/GDPR + ePrivacy compliant.
 *
 * Kernpunten:
 *   1. Geen tracking-scripts worden geladen vóór expliciete opt-in.
 *   2. Granulaire keuze per doelcategorie (functioneel / analytics / marketing).
 *      Functioneel is altijd aan (noodzakelijk voor werking) en telt niet
 *      als toestemming-plichtig conform AVG art. 6 lid 1 sub f.
 *   3. Google Consent Mode v2 (verplicht voor Google Ads EEA-verkeer sinds
 *      maart 2024) — defaults op 'denied', update naar 'granted' bij opt-in.
 *   4. Intrekken van toestemming is net zo makkelijk als geven — via de
 *      footer-link "Cookievoorkeuren" opent dezelfde modal opnieuw.
 *   5. Cookieverklaring en privacyverklaring zijn direct in de banner
 *      gelinkt (transparantie-eis AVG art. 12-14).
 */

const CONSENT_KEY = 'hoibuur_cookie_consent_v2';   // v2 = granular
const CONSENT_LEGACY_KEY = 'hoibuur_cookie_consent'; // v1 (all-or-nothing)
const CONSENT_VERSION = 2;

const GTAG_ID = 'AW-18096162059';
const GTM_CONTAINER_ID = 'GTM-56SLPPMV';
const META_PIXEL_ID = '2054083525139197';
const TIKTOK_PIXEL_ID = 'D7J1OUBC77UBV63HSMDG';

/* -----------------------------------------------------------------------
 * Google Consent Mode v2 — de 'default' (denied) staat wordt nu in
 * public/index.html gezet, VÓÓR de GTM-container laadt (Full Consent
 * Mode v2). Hier zorgen we enkel dat dataLayer/gtag beschikbaar zijn
 * als fallback; we pushen GEEN tweede 'default' meer om conflicten te
 * voorkomen.
 * --------------------------------------------------------------------- */
function initConsentMode() {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  if (!window.gtag) window.gtag = gtag;
}
initConsentMode();

/* -----------------------------------------------------------------------
 * Script loaders — pure side-effects, geen state.
 * --------------------------------------------------------------------- */
function loadGoogleTagManager() {
  if (document.querySelector(`script[src*="gtm.js?id=${GTM_CONTAINER_ID}"]`)) return;
  (function (w, d, s, l, i) {
    w[l] = w[l] || [];
    w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
    const f = d.getElementsByTagName(s)[0];
    const j = d.createElement(s);
    const dl = l !== 'dataLayer' ? '&l=' + l : '';
    j.async = true;
    j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
    f.parentNode.insertBefore(j, f);
  })(window, document, 'script', 'dataLayer', GTM_CONTAINER_ID);
}

function loadGoogleAds() {
  if (document.querySelector(`script[src*="gtag/js?id=${GTAG_ID}"]`)) return;
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GTAG_ID}`;
  document.head.appendChild(script);
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  if (!window.gtag) window.gtag = gtag;
  window.gtag('js', new Date());
  window.gtag('config', GTAG_ID);
}

function loadMetaPixel() {
  if (window.fbq) return;
  !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
  window.fbq('init', META_PIXEL_ID);
  window.fbq('track', 'PageView');
}

function loadTikTokPixel() {
  if (window.ttq) return;
  !function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"];ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e};ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{};ttq._i[e]=[];ttq._i[e]._u=r;ttq._t=ttq._t||{};ttq._t[e]=+new Date;ttq._o=ttq._o||{};ttq._o[e]=n||{};n=document.createElement("script");n.type="text/javascript";n.async=!0;n.src=r+"?sdkid="+e+"&lib="+t;e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};ttq.load(TIKTOK_PIXEL_ID);ttq.page();}(window,document,'ttq');
}

/* -----------------------------------------------------------------------
 * Consent state management
 * --------------------------------------------------------------------- */
export function getConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

function persistConsent(consent) {
  localStorage.setItem(CONSENT_KEY, JSON.stringify({
    version: CONSENT_VERSION,
    functional: true,
    analytics: !!consent.analytics,
    marketing: !!consent.marketing,
    ts: new Date().toISOString(),
  }));
  // Legacy sleutel opruimen om verwarring te voorkomen.
  try { localStorage.removeItem(CONSENT_LEGACY_KEY); } catch { /* noop */ }
}

function applyConsent(consent) {
  const analytics = !!consent.analytics;
  const marketing = !!consent.marketing;

  // Google Consent Mode v2 update
  if (window.gtag) {
    window.gtag('consent', 'update', {
      ad_storage: marketing ? 'granted' : 'denied',
      ad_user_data: marketing ? 'granted' : 'denied',
      ad_personalization: marketing ? 'granted' : 'denied',
      analytics_storage: analytics ? 'granted' : 'denied',
    });
  }

  // GTM levert GA4 (analytics) én Ads-tags (marketing). We laden GTM
  // alleen als één van beide categorieën 'granted' is; de tags in GTM
  // gehoorzamen vervolgens de Consent Mode signalen.
  if (analytics || marketing) {
    loadGoogleTagManager();
  }
  if (marketing) {
    loadGoogleAds();
    loadMetaPixel();
    loadTikTokPixel();
  }
}

/**
 * Global helper: open the cookie preferences modal again.
 * Wire this to a footer link, profile setting, etc.
 */
export function openCookieSettings() {
  window.dispatchEvent(new CustomEvent('hoibuur:open-cookie-settings'));
}

/* -----------------------------------------------------------------------
 * Tracking events — no-op tenzij consent gegeven is.
 * --------------------------------------------------------------------- */
export function trackConversion() {
  const c = getConsent();
  if (!c || !c.marketing) return;
  if (window.gtag) {
    window.gtag('event', 'conversion', {
      send_to: `${GTAG_ID}/wNlgCKmqsp0cEIuK9rRD`,
      value: 1.0,
      currency: 'EUR',
    });
  }
  if (window.fbq) window.fbq('track', 'CompleteRegistration');
  if (window.ttq) window.ttq.track('CompleteRegistration');
}

export function trackEvent(eventName, props = {}) {
  const c = getConsent();
  if (window.gtag && c?.analytics) window.gtag('event', eventName, props);
  if (window.fbq && c?.marketing) window.fbq('trackCustom', eventName, props);
  if (window.ttq && c?.marketing) window.ttq.track(eventName, props);
  // Own backend logging (server-side, first-party) mag altijd door — het is
  // eerste-partij logging voor legitiem belang (dienstverlening en fraude-
  // preventie). Bevat geen persoons-gerichte tracking-cookies.
  try {
    import('../utils/utm').then(({ getStoredUTM }) => {
      const utm = getStoredUTM();
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      if (!apiBase) return;
      const token = localStorage.getItem('hoibuur_token');
      if (!token) return;
      fetch(`${apiBase}/api/analytics/utm-event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ event_name: eventName, activity_id: props.activity_id || null, utm: utm || {} }),
      }).catch(() => {});
    });
  } catch { /* ignore */ }
}

/* -----------------------------------------------------------------------
 * UI Component
 * --------------------------------------------------------------------- */
const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analyticsChecked, setAnalyticsChecked] = useState(true);
  const [marketingChecked, setMarketingChecked] = useState(true);
  // canClose = true alleen wanneer de bezoeker al eerder een keuze maakte en de
  // modal via "Cookievoorkeuren" heropent. Bij het eerste bezoek is de modal
  // een verplichte keuze-poort: geen kruisje, niet weg te klikken.
  const [canClose, setCanClose] = useState(false);

  // Broadcast visibility zodat zwevende UI-elementen (Aa, ?) niet over de
  // Accepteren-knop staan.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('hoibuur:cookie-banner', { detail: { visible } }));
  }, [visible]);

  useEffect(() => {
    const consent = getConsent();
    if (consent && consent.version === CONSENT_VERSION) {
      applyConsent(consent);
      return;
    }
    // Migratie: legacy "accepted" wordt opgevat als "alles-aan" en direct
    // toegepast. Legacy "declined" wordt "alleen functioneel" (geen banner).
    try {
      const legacy = localStorage.getItem(CONSENT_LEGACY_KEY);
      if (legacy === 'accepted') {
        const migrated = { analytics: true, marketing: true };
        persistConsent(migrated);
        applyConsent(migrated);
        return;
      }
      if (legacy === 'declined') {
        persistConsent({ analytics: false, marketing: false });
        return;
      }
    } catch { /* ignore */ }
    const t = setTimeout(() => { setCanClose(false); setVisible(true); }, 800);
    return () => clearTimeout(t);
  }, []);

  // Externe trigger: iemand klikt op "Cookievoorkeuren" in de footer.
  useEffect(() => {
    const handler = () => {
      const existing = getConsent();
      setAnalyticsChecked(existing?.analytics ?? true);
      setMarketingChecked(existing?.marketing ?? true);
      setShowDetails(true);
      setCanClose(!!existing);
      setVisible(true);
    };
    window.addEventListener('hoibuur:open-cookie-settings', handler);
    return () => window.removeEventListener('hoibuur:open-cookie-settings', handler);
  }, []);

  const acceptAll = useCallback(() => {
    const c = { analytics: true, marketing: true };
    persistConsent(c);
    applyConsent(c);
    setVisible(false);
    setShowDetails(false);
  }, []);

  const declineAll = useCallback(() => {
    const c = { analytics: false, marketing: false };
    persistConsent(c);
    applyConsent(c);
    setVisible(false);
    setShowDetails(false);
  }, []);

  const saveSelection = useCallback(() => {
    const c = { analytics: analyticsChecked, marketing: marketingChecked };
    persistConsent(c);
    applyConsent(c);
    setVisible(false);
    setShowDetails(false);
  }, [analyticsChecked, marketingChecked]);

  const closeModal = useCallback(() => {
    setVisible(false);
    setShowDetails(false);
  }, []);

  // Body-scroll vergrendelen zolang de modal open is + Escape sluiten (alleen
  // wanneer sluiten is toegestaan).
  useEffect(() => {
    if (!visible) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape' && canClose) closeModal(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [visible, canClose, closeModal]);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
      data-testid="cookie-consent-overlay"
      onClick={() => { if (canClose) closeModal(); }}
    >
      <div
        className="relative w-full max-w-lg bg-white shadow-2xl rounded-2xl overflow-hidden animate-in zoom-in-95 fade-in duration-200 max-h-[90vh] overflow-y-auto"
        data-testid="cookie-consent-banner"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-title"
        onClick={(e) => e.stopPropagation()}
      >
        {canClose && (
          <button
            onClick={closeModal}
            className="absolute top-3 right-3 z-10 text-gray-400 hover:text-gray-600 p-1.5 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Sluiten"
            data-testid="cookie-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        )}

        {/* Header */}
        <div className="flex items-start gap-3 p-6 pb-3">
          <div className="w-11 h-11 rounded-full bg-hoi-coral/15 flex items-center justify-center flex-shrink-0">
            <Cookie className="w-5 h-5 text-hoi-coral" aria-hidden="true" />
          </div>
          <div className="flex-1 min-w-0 pr-6">
            <h2 id="cookie-title" className="font-heading font-semibold text-lg text-hoi-blue mb-1">
              Wij respecteren je privacy
            </h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              Wij gebruiken alleen noodzakelijke cookies om Hoi Buur Almere te laten werken. Alleen met jouw
              toestemming meten wij het gebruik van de site en het bereik van onze campagnes. De site werkt
              volledig, ook als je alleen noodzakelijke cookies kiest.
            </p>
            <p className="text-xs text-slate-500 mt-2">
              Lees meer in onze{' '}
              <a href="/privacyverklaring" className="text-hoi-blue underline hover:text-hoi-blue/80" data-testid="cookie-link-privacy">
                privacyverklaring
              </a>{' '}
              en{' '}
              <a href="/cookies" className="text-hoi-blue underline hover:text-hoi-blue/80" data-testid="cookie-link-cookies">
                cookieverklaring
              </a>.
            </p>
          </div>
        </div>

        {/* Details (uitgeklapt) */}
        {showDetails && (
          <div className="px-6 pb-4 border-t border-gray-100 pt-4 space-y-3" data-testid="cookie-details">
            <CategoryRow
              icon={<Shield className="w-4 h-4 text-slate-500" aria-hidden="true" />}
              title="Noodzakelijk"
              description="Onmisbaar voor inloggen, sessies en veiligheid. Kan niet worden uitgeschakeld."
              checked
              disabled
              testId="cookie-category-functional"
            />
            <CategoryRow
              icon={<BarChart3 className="w-4 h-4 text-slate-500" aria-hidden="true" />}
              title="Analytische cookies"
              description="Helpt ons het aantal bezoekers en de meest gebruikte pagina's te meten via Google Analytics. Geanonimiseerd, geen persoonlijke tracking."
              checked={analyticsChecked}
              onChange={(v) => setAnalyticsChecked(v)}
              testId="cookie-category-analytics"
            />
            <CategoryRow
              icon={<Megaphone className="w-4 h-4 text-slate-500" aria-hidden="true" />}
              title="Marketing & campagne-meting"
              description="Meten via Google Ads, Meta en TikTok of onze campagnes werken. Alleen aan met jouw toestemming."
              checked={marketingChecked}
              onChange={(v) => setMarketingChecked(v)}
              testId="cookie-category-marketing"
            />
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-2 px-6 pb-6 pt-3">
          <div className="flex flex-col sm:flex-row gap-2">
            {showDetails ? (
              <Button
                onClick={saveSelection}
                variant="outline"
                className="flex-1 border-gray-300 text-gray-700 font-heading text-sm h-11"
                data-testid="cookie-save-selection-btn"
              >
                Voorkeuren opslaan
              </Button>
            ) : (
              <Button
                onClick={declineAll}
                variant="outline"
                className="flex-1 border-gray-300 text-gray-700 font-heading text-sm h-11"
                data-testid="cookie-decline-btn"
              >
                Alleen noodzakelijk
              </Button>
            )}
            <Button
              onClick={acceptAll}
              className="flex-1 bg-hoi-coral hover:bg-hoi-coral/90 text-hoi-black font-heading font-semibold text-sm h-11"
              data-testid="cookie-accept-btn"
            >
              Alles accepteren
            </Button>
          </div>
          <button
            onClick={() => setShowDetails((v) => !v)}
            className="text-sm text-slate-600 hover:text-hoi-blue underline underline-offset-2 text-center mt-1"
            data-testid="cookie-toggle-details-btn"
          >
            {showDetails ? 'Details verbergen' : 'Voorkeuren aanpassen'}
          </button>
        </div>
      </div>
    </div>
  );
};

const CategoryRow = ({ icon, title, description, checked, disabled, onChange, testId }) => (
  <div className="flex items-start gap-3" data-testid={testId}>
    <div className="w-7 h-7 rounded-md bg-slate-50 flex items-center justify-center flex-shrink-0 mt-0.5">
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-heading font-semibold text-sm text-slate-900">{title}</h3>
        <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={!!checked}
            disabled={disabled}
            onChange={(e) => onChange && onChange(e.target.checked)}
            data-testid={`${testId}-toggle`}
          />
          <div className={`w-9 h-5 rounded-full transition-colors ${checked ? 'bg-hoi-coral' : 'bg-slate-300'} ${disabled ? 'opacity-60' : ''} peer-focus-visible:ring-2 peer-focus-visible:ring-hoi-coral/40`}>
            <div className={`absolute top-0.5 ${checked ? 'left-[18px]' : 'left-0.5'} w-4 h-4 bg-white rounded-full shadow transition-all`} />
          </div>
        </label>
      </div>
      <p className="text-xs text-slate-500 mt-1 leading-relaxed">{description}</p>
    </div>
  </div>
);

export default CookieConsent;
